/* global _, ALKMaps */
const config = require("config");
const debug = require("debug")("compliance:service");
import angular from "angular";
import { deriveRoute } from "./deriveRoute";
const routeColors = ["#123EC4", "#B84A00", "#7318A1", "#1E6CA8", "#B50E59", "#450DBD", "#009DC4", "#E63D00"]; //route color properties

angular
  .module("fleetviewApp")
  .service("complianceService", ComplianceService);

ComplianceService.$inject = [
  "$http",
  "moment",
  "RoutingProfiles",
  "mapUtilities",
  "cacheService",
  "ComplianceRoutingProfileService",
  "UserProvider",
  "$q",
  "complianceMapService",
  "$location",
  "timeService",
  "$timeout",
  "RouteReporterAPI"
];

function ComplianceService($http, moment, RoutingProfiles, mapUtilities, cacheService, ComplianceRoutingProfileService,
  UserProvider, $q, complianceMapService, $location, timeService, $timeout, RouteReporterAPI) {
  let currentColor = 0;
  let vehicleGroupRoutingProfiles = [];
  let $ctrl = {};
  // array containing the route id's and visibility properties for that route
  // each array item (a route) is of the form { tripId (number), isVisible (boolean) }
  let routeVisibilityList = [];

  const service = {
    getComplianceData: getComplianceData,
    mapRoute: mapRoute,
    changeProfile: changeProfile,
    noRoutingProfiles,
    getRoutePage: getRoutePage,
    getRouteVisibilityList: getRouteVisibilityList,
    setRouteVisibility: setRouteVisibility,
    initRoutes: initRoutes
  };

  return service;

  /**
     * @example
     *  {
          "accountId": 1,
          "groupId": 24535,
          "ids": 1061,
          "type": "vehicle",
          "startDate": "8/1/2014",
          "endDate": "9/1/2014",
          "page": 1,
          "pageSize": 10,
          "noCoords": false,
          "oorCost": 0.77,
          "sort": "VehicleName",
          "order": "asc",
          "noQuestionableRoutes": true,
          "includeRoutesWithNoOorMiles": true
        }
    * @param {any} params post data
    * @returns A list of compliance data
    *
    * @memberOf ComplianceService
    * @returns {object} compliance data
    */
  function getComplianceData(params) {
    return RouteReporterAPI.post("api/Report", params, null).then(
      response => response.data
    );
  }

  /**
     * Gets the array filled with route visibility data, where each item is in the format of 
     * {
     *  tripId: number,
     *  isVisible: boolean
     * }
     * @returns {Array} route visibility array
     */
  function getRouteVisibilityList() {
    return routeVisibilityList;
  }

  /**
     * Sets the visibility of the route.
     * @param {number} idx index in the route visibility array
     * @param {boolean} isVisible whether the route should be visible on the map
     */
  function setRouteVisibility(idx, isVisible) {
    routeVisibilityList[idx].isVisible = isVisible;
  }

  /**
     * Converts a route returned from the compliance data to a format usable for this component.
     * @param {any} route
     * @returns promise
     */
  //  TODO: clean this up
  function mapRoute(route, shouldDraw) { // eslint-disable-line complexity
    const routeFound = routeVisibilityList.some(el => el.tripId === route.tripId);
    if (!routeFound) {
      let r = {};
      r.tripId = route.tripId;
      r.isVisible = true;
      routeVisibilityList.push(r);
    }

    const r = {};
    const stops = {};
    r.pointsFormatted = false;
    r.tripId = route.tripId;
    r.isVisible = true;
    r.routeColor = routeColors[currentColor];
    r.routeColorClass = `routeColor${currentColor}`;
    r.isCancelled = route.effectedRoute.canceled;
    r.questionable = route.effectedRoute.questionable;
    r.oorCost = route.oorCost;
    r.routingProfileId = route.prescribedRoute.routingProfileId;

    r.routeSource = route.prescribedRoute.routeSource.toUpperCase();
    if (r.routeSource !== "MANUAL" && r.routeSource !== "INTEGRATION" && r.routeSource !== "ROUTESYNC" && r.routeSource !== "DISPATCH") {
      r.routeSource = "UNKNOWN";
    }
    currentColor++;
    if (currentColor >= 8) {
      currentColor = 0;
    }
    r.driverName = route.driverName;
    r.driverId = route.prescribedRoute.driverId;
    r.vehicleName = route.vehicleName; // NOTE: used to display Vehicle Name (aka ScreenName)
    r.vehicleId = route.deviceId; // NOTE: used to display deviceId as Vehicle ID
    r.unitId = route.unitId; // NOTE: this is internal [TVehicle.VehicleId] used for getVehicleRoutingProfiles() in fleetlink.routing-profiles.js
    r.showStops = false;

    var originStop = null;
    var destinationStop = null;
    if (route.prescribedRoute.tripStops.length > 0) {
      originStop = route.prescribedRoute.tripStops[0];
      destinationStop = route.prescribedRoute.tripStops[route.prescribedRoute.tripStops.length - 1];
      r.originStopName = originStop.stopName;
      r.originStreet = originStop.street;
      r.originCity = originStop.city;
      r.originState = originStop.state;
      r.originZip = originStop.zip;
      r.originOffset = route.effectedRoute.routeStartUtcOffset;
      r.originTime = timeService.getFormattedTime2(route.effectedRoute.routeStart, route.effectedRoute.routeStartUtcOffset);
      if (route.effectedRoute.routeStartUtcOffset) {
        r.originTimeStamp = route.effectedRoute.routeStart;
      }
      r.originLonLat = new ALKMaps.LonLat(originStop.longitude, originStop.latitude).transform(new ALKMaps.Projection("EPSG:4326"), new ALKMaps.Projection("EPSG:900913"));
      r.originCoords = `${originStop.latitude}, ${originStop.longitude}`;
      r.destinationStopName = destinationStop.stopName;
      r.destinationStreet = destinationStop.street;
      r.destinationCity = destinationStop.city;
      r.destinationState = destinationStop.state;
      r.destinationZip = destinationStop.zip;
      r.destinationOffset = route.effectedRoute.routeEndUtcOffset;
      r.destinationTime = timeService.getFormattedTime2(route.effectedRoute.routeEnd, route.effectedRoute.routeEndUtcOffset);
      if (route.effectedRoute.routeEndUtcOffset) {
        r.destinationTimeStamp = route.effectedRoute.routeEnd;
      }
      r.destinationLonLat = new ALKMaps.LonLat(destinationStop.longitude, destinationStop.latitude)
        .transform(new ALKMaps.Projection("EPSG:4326"), new ALKMaps.Projection("EPSG:900913"));
      r.destinationCoords = `${destinationStop.latitude}, ${destinationStop.longitude}`;
      if (route.prescribedRoute.tripStops.length > 2) {
        for (var i = 1; i < route.prescribedRoute.tripStops.length - 1; i++) {
          stops[i] = {};
          stops[i].stopNumber = i;
          stops[i].stopName = route.prescribedRoute.tripStops[i].stopName;
          stops[i].stopStreet = route.prescribedRoute.tripStops[i].street;
          stops[i].stopCity = route.prescribedRoute.tripStops[i].city;
          stops[i].stopState = route.prescribedRoute.tripStops[i].state;
          stops[i].stopZip = route.prescribedRoute.tripStops[i].zip;
          stops[i].stoplonLat = new ALKMaps.LonLat(route.prescribedRoute.tripStops[i].longitude, route.prescribedRoute.tripStops[i].latitude).transform(new ALKMaps.Projection("EPSG:4326"), new ALKMaps.Projection("EPSG:900913"));
          stops[i].stopCoords = `${route.prescribedRoute.tripStops[i].latitude} , ${route.prescribedRoute.tripStops[i].longitude}`;
        }

        r.stops = stops;
        r.stopCount = route.prescribedRoute.tripStops.length - 2;
      }
    } else {
      console.log("no trips stops! todo: log error. ", route);
    }
    r.distancePlanned = route.prescribedRoute.distance;
    r.distanceActual = route.effectedRoute.distance;
    r.distanceDifference = route.distanceDelta;
    r.status = "";
    if (r.isCancelled) {
      r.status = "Cancelled";
    } else if (r.distanceActual > 0) {
      r.status = "Ended";
    } else {
      r.status = "Incomplete";
    }
    r.timePlanned = moment.duration(route.prescribedRoute.time / 1000, "hours").format("h:mm", {
      trim: false
    });
    r.timeActual = moment.duration(route.effectedRoute.time / 1000, "hours").format("h:mm", {
      trim: false
    });
    r.timeDifference = moment.duration(route.timeDelta / 1000, "hours").format("h:mm", {
      trim: false
    });
    r.timeDifference = r.timeDifference === "-0:00" ? "0:00" : r.timeDifference;
    r.isDelayed = r.timePlanned < r.timeActual ? true : false;
    r.oorDistance = route.oorDistance;
    r.isOor = route.outOfRouteEvents.events.length > 0 ? `${route.outOfRouteEvents.events.length} found` : "None";
    r.origStopList = route.prescribedRoute.tripStops;
    r.stopList = _.map(route.prescribedRoute.tripStops, (stop) => {
      return new ALKMaps.LonLat(stop.longitude, stop.latitude).transform(new ALKMaps.Projection("EPSG:4326"), new ALKMaps.Projection("EPSG:900913"));
    });
    r.outOfRouteEvents = [];
    if (route.outOfRouteEvents.events.length > 0) {
      r.outOfRouteEvents = _.map(route.outOfRouteEvents.events, (e) => {
        return {
          type: e.type,
          oorLon: e.lon,
          oorLat: e.lat,
          lonlat: new ALKMaps.LonLat(e.lon, e.lat).transform(new ALKMaps.Projection("EPSG:4326"), new ALKMaps.Projection("EPSG:900913")),
          point: new ALKMaps.Geometry.Point(e.lon, e.lat).transform(new ALKMaps.Projection("EPSG:4326"), new ALKMaps.Projection("EPSG:900913")),
          latlong: new ALKMaps.LonLat(e.lat, e.lon).transform(new ALKMaps.Projection("EPSG:4326"), new ALKMaps.Projection("EPSG:900913")),
          distanceOOR: e.distanceOOR,
          timeStamp: timeService.getFormattedTime2(e.timeStamp, e.timeStampUtcOffset),
          timeStampUtcOffset: e.timeStampUtcOffset,
          oorStreetAddr: e.streetAddress,
          oorCity: e.city,
          oorState: e.state,
          oorZip: e.zip,
          oorHeading: e.heading,
          oorAltitude: e.altitude,
          oorSpeed: e.speed
        };
      });
    }
    r.outOfRouteEvents = r.outOfRouteEvents.reverse();
    let routingProfilePromise;

    r.externalTripId = route.prescribedRoute.externalTripId;
    const { routingProfileId, prescribedRouteId } = route.prescribedRoute;
    const { routingProfileFromPrescribedRoute } = route;
    //First try routing profile of the trip itself
    if (routingProfileId) {
      var currentUser = UserProvider.getUser();
      var options = { cache: true };

      if (currentUser.supportAccount) {
        options.params = {};
        options.params.targetAccount = currentUser.supportAccount.accountId;
      }
      routingProfilePromise = RoutingProfiles.getRoutingProfile(routingProfileId, options);
    } else {
      routingProfilePromise = getBackupRPPromise(r);
    }
    //change format of time & lat lon for positions
    if (!route.pointsFormatted && route.effectedRoute.positions) {
      route.effectedRoute.positions.forEach(point => {
        point.Ln = Number(point.Ln) / 1e6;
        point.Lt = Number(point.Lt) / 1e6;
        point.timeStamp = timeService.getEpochFormattedTime(point.DTS, point.O);
      });

      route.pointsFormatted = true; // format points only once
    }

    // gets flagged as "DriverProfile" & added to r.routingProfile[] for the drop-down
    // beware of side-effects by moving this elsewhere
    if (routingProfileFromPrescribedRoute && !routingProfileFromPrescribedRoute.routingProfileId) {
      routingProfileFromPrescribedRoute.source = "DriverProfile";
      debug("routingProfileFromPrescribedRoute:", routingProfileFromPrescribedRoute);
      r.routingProfileFromPrescribedRoute = routingProfileFromPrescribedRoute;
    }

    //Add start and end destination points to beginning and end of derived and prescribed route points if they exist
    // TODO: extract into own function
    if (route.prescribedRoute && route.prescribedRoute.coords && route.prescribedRoute.coords.length) {
      if (originStop) {
        const originPoint = [originStop.longitude, originStop.latitude];
        const hasOrigin = _.some(route.prescribedRoute.coords, originPoint);
        if (!hasOrigin) {
          route.prescribedRoute.coords = [originPoint, ...route.prescribedRoute.coords];
        }

        if (route.effectedRoute && route.effectedRoute.positions) {
          route.effectedRoute.positions.unshift(originPoint);
        }
      }
      if (destinationStop) {
        var destinationPoint = [destinationStop.longitude, destinationStop.latitude];
        const hasDest = _.some(route.prescribedRoute.coords, destinationPoint);
        if (!hasDest) {
          route.prescribedRoute.coords = [...route.prescribedRoute.coords, destinationPoint];
        }

        if (route.effectedRoute && route.effectedRoute.positions && route.effectedRoute.positions.length >= 1) {
          var lastPing = route.effectedRoute.positions[route.effectedRoute.positions.length - 1];
          var destGeom = new ALKMaps.Geometry.Point(destinationStop.longitude, destinationStop.latitude);
          const lastPingGeom = new ALKMaps.Geometry.Point(lastPing.Ln, lastPing.Lt);
          var line = new ALKMaps.Geometry.LineString([destGeom, lastPingGeom]);
          var distance = line.getGeodesicLength(new ALKMaps.Projection("EPSG:4326"));
          distance *= 0.001; //meters to km
          //0.321869 km = 0.2 mi
          if (distance < 0.321869) {
            route.effectedRoute.positions.push(destinationPoint);
          }
        }
      }
    }

    r.origPlannedRoutePoints = route.prescribedRoute.coords.slice(1, -1); // remove 1st & last point
    r.origPlannedConnectorPoints = {
      origin: _.take(route.prescribedRoute.coords, 2), // first 2 points
      destination: _.takeRight(route.prescribedRoute.coords, 2) // last 2 points
    };
    r.origActualRoutePoints = []; //used to derive route
    if (route.effectedRoute.positions) {
      route.effectedRoute.positions.forEach(point => {
        if (point.Ln) {
          r.origActualRoutePoints.push([point.Ln, point.Lt]);
        } else {
          r.origActualRoutePoints.push(point);
        }
      });
    }
    r.gpsPositions = route.effectedRoute.positions || route.effectedRoute.coords; //used in map service

    const onRPGetSuccess = function (response) {
      const results = response;
      //routing profile object returned
      if (_.isObject(results) && !Array.isArray(results)) { // ensure its an Object and not Array
        r.routingProfiles = [results];
        if (r.routingProfileId) {
          r.routingProfiles[0].source = "CompanyProfile";
          // r.selectedRoutingProfile = r.routingProfiles[0]; // TODO: clean up
        }
      }
      //Array of routing profiles returned; either VG or Account profiles
      else if (Array.isArray(results) && _.some(results)) {
        r.routingProfiles = angular.copy(results);
      } else {
        r.routingProfiles = [];
      }
      // if RP from prescribedRoute exists, set as 'selected'
      if (!r.selectedRoutingProfile && r.routingProfileFromPrescribedRoute) {
        r.selectedRoutingProfile = r.routingProfileFromPrescribedRoute;
      }

      debug("onRPGetSuccess()", r.selectedRoutingProfile, r)
      return results;
    };

    //Getting the routing profile associated with the trip may sometimes fail if it's outdated, so use the backup rps from the vehicle group
    return routingProfilePromise.then(onRPGetSuccess, (error) => {
      if (error) {
        console.error("routingProfilePromise()", error); // eslint-disable-line no-console
      }
      return getBackupRPPromise(r).then(onRPGetSuccess, (errorBackup) => {
        return null;
      });
    }).then(() => {
      setRoughRoutePoints(r); //When the page first loads, set rough line strings to avoid waiting for routePath call
      if (shouldDraw) {
        complianceMapService.drawRoute(r); //Draw rough route at first.
        setProfile(r); //Change the profile - this will asynchronously load and re-draw the aligned route in the background.
      }
      return r;
    });
  }
  // <-- END OF mapRoute() --> //

  //Called when the routing profile dropdown changes. Uses the selected routing profile to re-draw the routes on the map according to that routing profile.
  function changeProfile(route) {
    debug("changeProfile event")
    setProfile(route);
  }

  function setProfile(route) {
    route.isChangingProfile = true;
    route.routingProfileError = false;
    const rp = route.selectedRoutingProfile;
    debug("setProfile() -> selectedRoutingProfile:", rp, "route:", route);
    const deriveRouteDeps = { $q, cacheService, ComplianceRoutingProfileService, UserProvider, mapUtilities }

    // TODO: use tank-mode when no rp is available
    const actualRoutePromise = deriveRoute(route.origActualRoutePoints, rp, route.tripId, 1, deriveRouteDeps);
    const plannedRoutePromise = deriveRoute(route.origPlannedRoutePoints, rp, route.tripId, 0, deriveRouteDeps);

    $q.all([actualRoutePromise, plannedRoutePromise]).then((results) => {
      route.actualRoutePoints = results[0];
      route.plannedRoutePoints = results[1];
      debug("plannedRoutePromise() -> results:", route.plannedRoutePoints.length);
      // debug("setProfile()-> plannedRouteFeature.geometry:", route.complianceObj.plannedRouteFeature.geometry.components);
      complianceMapService.redrawRoute(route);
      route.isChangingProfile = false;
      route.routingProfileError = false;
    }, (error) => {
      route.isChangingProfile = false;
      route.routingProfileError = true;
    });
  }

  function noRoutingProfiles(route) {
    return _.isEmpty(route.routingProfiles);
  }

  //Set rough route points for the actual/planned routes when the page first loads.
  function setRoughRoutePoints(route) {

    function convertLLtoMerc(latLon) {
      const merc = mapUtilities.LLtoMerc(latLon[0], latLon[1]);
      return new ALKMaps.Geometry.Point(merc.lon, merc.lat);
    }

    route.actualRoutePoints = _.map(route.origActualRoutePoints, convertLLtoMerc);
    route.plannedRoutePoints = _.map(route.origPlannedRoutePoints, convertLLtoMerc);
    const origin = _.map(route.origPlannedConnectorPoints.origin, convertLLtoMerc);
    const destination = _.map(route.origPlannedConnectorPoints.destination, convertLLtoMerc);
    route.plannedConnectorPoints = { origin, destination };
  }

  function getBackupRPPromise(r) {
    const user = UserProvider.getUser();
    const options = { cache: true };
    if (user.supportAccount) {
      options.params = { targetAccount: user.supportAccount.accountId };
    }

    return RoutingProfiles.getVehicleRoutingProfiles(r.unitId, options).then(results => {
      debug("### getBackupRPPromise() => getVehicleRoutingProfiles()", results)
      if (results && results.length > 0) {
        const rps = results.map(rp => {
          rp.source = "CompanyProfile_BackupVG";
          return rp;
        })
        return rps;
      } else {
        //If that doesn't exist, last fallback is the account routing profiles.
        debug("using accountRoutingProfiles!", $ctrl.accountRoutingProfiles);
        return $ctrl.accountRoutingProfiles;
      }
    }, (error) => {
      console.error(`getVehicleRoutingProfiles() ${error}`); // eslint-disable-line no-console
    });
  }


  /**
     * Get and draw a list of routes for a specific page.
     * @param {object} vm view model
     * @param {object} searchSettings options
     * @param {int} pageNumber page index
     * @param {int} pageIndex is optional - if set will only draw route at that index
     * @returns {Promise} object representing the completion of querying the service and setting the returned routes on the model.
     */
  function getRoutePage(vm, searchSettings, pageNumber, pageIndex) {
    vm.isLoading = true;

    //Wrap this in a timeout after isLoading so the isLoading spinner always displays even when loading from cache
    //$timeout returns a promise.
    return $timeout(() => {

      complianceMapService.clearMap();

      const user = JSON.parse(window.sessionStorage["userAuthInfo"]);

      let accountId = user.accountId;
      const currentUser = UserProvider.getUser();
      if (currentUser.supportAccount) {
        accountId = currentUser.supportAccount.accountId;
      }

      const p = {
        "accountId": accountId,
        "pageNumber": pageNumber,
        "pageSize": 10,
        "noCoords": false,
        "oorCost": 0.0,
        "sort": "VehicleName",
        "order": "asc",
        "isOor": false,
        "hideCoords" : false
      };

      const allParams = angular.merge({}, p, searchSettings);

      //HTTP POST calls cannot be cached by angular's $http (only GET). Must use custom cacheService to cache instead.
      //Cache compliance calls by page number.
      const cachedResponse = cacheService.getCachedRouteResponse(pageNumber);
      const getComplianceDataCall = (cachedResponse) ? $q.when(cachedResponse) : getComplianceData(allParams);

      return getComplianceDataCall.then(resp => {
        cacheService.setCachedRouteResponse(pageNumber, resp);
        if (resp.results === null || resp.results.routes.length === 0) {
          vm.routeList = [];
          vm.totalRoutesNum = 0;
          vm.totalPagesNum = 0;
          vm.isLoading = false;
          return;
        }
        resp.results.routes = removeDuplicateRoutes(resp.results.routes);
        vm.totalRoutesNum = resp.results.totalRoutes;
        $location.search("pageNumber", vm.currentPageNum);
        vm.totalPagesNum = resp.results.totalPages;

        vm.firstItemNum = (vm.currentPageNum * 10) - 9;
        vm.lastItemNum = ((vm.currentPageNum * 10) > vm.totalRoutesNum) ? vm.totalRoutesNum : (vm.currentPageNum * 10);

        const getRoutesWithRoutingProfile = resp.results.routes.map(route => {
          if (route.prescribedRoute.prescribedRouteId) {
            return ComplianceRoutingProfileService.getRoutingProfileByPrescribedRouteId(route.prescribedRoute.prescribedRouteId)
              .then(rp => {
                route.routingProfileFromPrescribedRoute = rp.data;
                return route;
              });
          }
          return Promise.resolve(route);
        });

        // Fetch routingProfile for each route using prescribedRouteId and then mapRoute() each route
        return Promise.all(getRoutesWithRoutingProfile)
          .then(routes => {
            const mapRoutePromises = resp.results.routes.map((route, index) => {
              let shouldDraw = false;
              const pageIndexNotSet = (pageIndex === undefined || pageIndex === null);
              if (pageIndexNotSet || (!pageIndexNotSet && index === pageIndex)) {
                shouldDraw = true;
              }
              return mapRoute(route, shouldDraw);
            });
            return Promise.all(mapRoutePromises);
          })
          .then(resultsArray => {
            vm.routeList = resultsArray;
            complianceMapService.frameAllRoutes();
            vm.isLoading = false;
          });
      });
    }, 100);
  }

  function removeDuplicateRoutes(routes) {
    const _routes = _.uniq(routes.reverse(), "tripId");
    return _routes.reverse();
  }

  //Get and set routes for the first time. (Calls getRoutePage under the hood.) Needed to initialize routing profiles.
  //This "resets" the service. Should be called on init of any controller that needs it.
  //pageIndex is optional integer. If set, will only draw that route index (zero indexed).
  function initRoutes(vm, searchSettings, pageNumber, pageIndex) {
    $ctrl = vm;
    vehicleGroupRoutingProfiles = [];
    currentColor = 0;
    const options = { cache: true };
    const user = UserProvider.getUser();
    if (user.supportAccount) {
      options.params = {};
      options.params.targetAccount = user.supportAccount.accountId;
    }

    return RoutingProfiles.getDefaultRoutingProfile(options).then(results => {
      $ctrl.accountRoutingProfiles = results.map(rp => {
        rp.source = "Account";
        return rp;
      });
      debug("getDefaultRoutingProfile() => accountRoutingProfiles", $ctrl.accountRoutingProfiles);
      //Then get routing profiles from the vehicle group id, if it is available
      if (vm.currentState !== "querystring" && searchSettings.idType === "vehicle") {
        const vehGroupId = searchSettings.groupId[0];
        return RoutingProfiles.getRoutingProfiles(vehGroupId, options);
      }
      //Otherwise return empty array for it
      else {
        return $q.when([]);
      }
    }).then(results => {
      vehicleGroupRoutingProfiles = results.map(rp => {
        rp.source = "CompanyProfile_VG";
        return rp;
      });
      if (vehicleGroupRoutingProfiles.length) {
        $ctrl.accountRoutingProfiles = []; // prevent showing the account default RPs
      }
      debug("getDefaultRoutingProfile() =>  vehicleGroupRoutingProfiles", vehicleGroupRoutingProfiles, $ctrl);
      return getRoutePage(vm, searchSettings, pageNumber, pageIndex);
    });
  }
}



// WEBPACK FOOTER //
// ./app/compliance/compliance.service.js